import { InvoiceStatus, OrderStatus, PurchaseOrderStatus } from "@prisma/client";

import { postedInvoiceStatuses } from "~/shared/orders/order-billing-presentation";

import {
  ActionOrder,
  billsBlockDemoteToOrderStatus,
  checkForDirectShip,
  checkForMarkCompleted,
  checkForReverseMarkCompleted,
  checkForVoidOrder,
  checkLotsForShip,
  paidOrPartiallyPaidInvoiceStatuses,
  reverseSubmitBillBlockMessage,
} from "./order-actions-text";

const draftStatusInvoiceMessage = "Must void invoice to return to Draft status";
const reverseShipInvoiceMessage = "Must void invoice first before reversing shipment";
const voidOrderBillMessage = "Must void bill before voiding this document";
const reverseMarkCompletedInvoiceMessage = "Reverse the payment to revert this order from Completed";

/**
 * Returns the human-readable tooltip message explaining why a status transition
 * is unavailable. Returns an empty string when the transition is allowed or when
 * the target is the current status (to avoid disabling the selected option).
 *
 * @param order        The order being evaluated.
 * @param status       The target status the user is hovering / attempting.
 * @param blockedMessage  Pre-formatted string from the external blockers system
 *                        (e.g. "2/2 checks failing"). Used as supplemental context.
 */
export const getOrderStatusBlockerMessage = (order: ActionOrder, status: OrderStatus, blockedMessage: string): string => {
  // Never return a blocker message for the current status — it would disable the selected option
  if (status === order.status) {
    return "";
  }

  const statusIsDraft = status === OrderStatus.DRAFT;
  const orderStatusIsNotDraft = order.status !== OrderStatus.DRAFT;

  if (statusIsDraft && orderStatusIsNotDraft) {
    const hasActiveInvoices = order.invoices.some(i => i.status !== InvoiceStatus.VOID);
    if (hasActiveInvoices) {
      return draftStatusInvoiceMessage;
    }
    if (billsBlockDemoteToOrderStatus(order, OrderStatus.DRAFT, PurchaseOrderStatus.DRAFT)) {
      return reverseSubmitBillBlockMessage;
    }
    // Note: Lots are auto-deallocated when reversing - no blocker needed
  }

  const statusIsConfirmed = status === OrderStatus.CONFIRMED;
  const orderStatusIsDraft = order.status === OrderStatus.DRAFT;
  if (statusIsConfirmed && orderStatusIsDraft && !!blockedMessage) {
    return blockedMessage;
  }

  // Reverse-ship (SHIPPED → CONFIRMED): both statuses are billable — only posted invoices block.
  const orderStatusIsShipped = order.status === OrderStatus.SHIPPED;
  if (statusIsConfirmed && orderStatusIsShipped) {
    const hasPostedInvoices = order.invoices.some(i => postedInvoiceStatuses.includes(i.status));
    if (hasPostedInvoices) {
      return reverseShipInvoiceMessage;
    }
  }

  if (status === OrderStatus.COMPLETED && order.status === OrderStatus.DELIVERED) {
    if (checkForMarkCompleted(order) === null) {
      const nonVoidInvoices = order.invoices.filter(i => i.status !== InvoiceStatus.VOID);
      if (nonVoidInvoices.length === 0) {
        return "Order must have an invoice to be completed";
      }
      const domainMessage = "All invoices must be paid to complete the order";
      return blockedMessage ? `${domainMessage} (${blockedMessage})` : domainMessage;
    }
  }

  // SC-1648: Completed → Delivered is the one demote a completed order allows. It is offered only
  // once no invoice carries an applied payment (i.e. the AR payment was reversed / voided), which
  // mirrors the invoice gate that granted COMPLETED in the first place.
  if (order.status === OrderStatus.COMPLETED) {
    if (status !== OrderStatus.DELIVERED) {
      return "Order is completed and cannot be changed";
    }
    if (checkForReverseMarkCompleted(order) !== null) {
      return "";
    }
    // Only say "reverse the payment" when a payment is what's in the way. The gate also refuses a
    // read-only order, one with no delivery date, and one completed without a direct invoice
    // (consignment) — none of which the user can fix by reversing anything.
    const hasAppliedPayment =
      order.invoices.some(i => paidOrPartiallyPaidInvoiceStatuses.includes(i.status)) ||
      order.items.some(item => (item.invoiceItems ?? []).some(ii => paidOrPartiallyPaidInvoiceStatuses.includes(ii.invoice.status))) ||
      order.items.some(item =>
        (item.invoiceOrderItems ?? []).some(
          ioi => "invoiceItem" in ioi && paidOrPartiallyPaidInvoiceStatuses.includes(ioi.invoiceItem.invoice.status),
        ),
      );
    return hasAppliedPayment ? reverseMarkCompletedInvoiceMessage : "Order is completed and cannot be changed";
  }

  if (status === OrderStatus.SHIPPED && order.status === OrderStatus.CONFIRMED) {
    if (checkLotsForShip(order) === null && checkForDirectShip(order) === null) {
      return blockedMessage || "All items must have lots or reservations assigned before shipping";
    }
  }

  if (status === OrderStatus.VOID) {
    if (checkForVoidOrder(order) === null) {
      const hasBillBlock = billsBlockDemoteToOrderStatus(order, OrderStatus.VOID, PurchaseOrderStatus.VOID);
      const hasNonVoidInvoices = order.invoices.some(i => i.status !== InvoiceStatus.VOID);
      if (hasBillBlock && hasNonVoidInvoices) {
        return "Void the invoice and bill before voiding the order";
      }
      if (hasBillBlock) {
        return voidOrderBillMessage;
      }
      return blockedMessage || "Void the invoice before voiding the order";
    }
  }

  return "";
};
